/**
 * MyForks — list page for the authenticated user's chain forks.
 * Status-grouped: Private (editable), Submitted (under review), Needs Revision,
 * Published.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Loader2, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const STATUS_LABEL = {
  private: 'PRIVATE · EDITABLE',
  submitted: 'SUBMITTED · UNDER REVIEW',
  needs_revision: 'NEEDS REVISION',
  published: 'PUBLISHED',
};
const STATUS_COLOR = {
  private: 'text-white/65',
  submitted: 'text-[#2F8A92]',
  needs_revision: 'text-amber-300',
  published: 'text-emerald-300',
};

export default function MyForks() {
  const navigate = useNavigate();
  const [forks, setForks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/forks/me');
        setForks(r.data?.forks || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load your forks.');
      }
      setBusy(false);
    })();
  }, []);

  if (busy) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <Loader2 size={20} className="animate-spin text-[#2F8A92]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white pb-16" style={{ background: '#0A0A0A' }} data-testid="my-forks-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch size={18} className="text-[#D4A84A]" />
          <span className="font-hud text-xs tracking-widest uppercase text-[#D4A84A]">My Forks</span>
        </div>
        <h1 className="font-historical text-3xl md:text-5xl text-white font-light leading-tight">
          Your private edits, your shipped revisions.
        </h1>
        <p className="font-mono text-sm md:text-base text-white/65 mt-3 max-w-2xl">
          Forks are private until you submit them. Submitted forks are reviewed by an admin —
          accepted forks become published chains with attribution to the original.
        </p>

        {error && <div className="mt-6 font-mono text-sm text-red-300" data-testid="my-forks-error">{error}</div>}

        {!error && forks.length === 0 && (
          <div className="mt-10 border border-dashed border-[#D4A84A]/30 rounded p-8 text-center" data-testid="my-forks-empty">
            <p className="font-mono text-sm md:text-base text-white/65 mb-4">
              No forks yet. Open any locked chain and click <span className="text-[#D4A84A]">FORK</span> to create one.
            </p>
            <button
              onClick={() => navigate('/welcome')}
              className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85"
            >
              EXPLORE CHAINS
            </button>
          </div>
        )}

        <ul className="mt-8 space-y-3" data-testid="my-forks-list">
          {forks.map((f) => (
            <li
              key={f.id}
              data-testid={`my-fork-row-${f.id}`}
              onClick={() => navigate(`/forks/${f.id}`)}
              className="cursor-pointer border border-[#D4A84A]/15 rounded p-4 hover:border-[#D4A84A]/55 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className={`font-mono text-[10px] tracking-widest uppercase mb-1 ${STATUS_COLOR[f.fork_status] || 'text-white/65'}`}>
                    {STATUS_LABEL[f.fork_status] || f.fork_status}
                  </div>
                  <div className="font-historical text-lg md:text-xl text-[#D4A84A] leading-snug">{f.forked_from_chain_title}</div>
                  <div className="font-mono text-xs text-white/55 mt-1">
                    {f.modified_steps?.length || 0} step(s) modified · updated {new Date(f.updated_at).toLocaleString()}
                  </div>
                </div>
                <ArrowRight size={14} className="text-[#D4A84A] flex-shrink-0" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
